<template>
  <div
    class="mt-3"
    data-testid="staff-table"
  >
    <div class="d-flex justify-content-between align-items-center">
      <h1 class="m-0 mr-3 h2">
        <template v-if="isCurrentStaff">
          Current Staff
        </template>
        <template v-else>
          Archived Staff
        </template>
      </h1>
      <div class="d-flex justify-content-end mb-2">
        <f-button
          v-if="!isCurrentStaff"
          text="Current Staff"
          class="mr-2"
          data-testid="current-staff-button"
          @click="toggleCurrentStaff"
        />
        <f-button
          v-else
          text="Archived Staff"
          class="mr-2"
          data-testid="archived-staff-button"
          @click="toggleCurrentStaff"
        />
        <f-button
          v-if="canManageSystem"
          text="Add Staff Member"
          type="primary-alt"
          icon="plus"
          class="add-new-staff mr-2"
          data-testid="add-staff-button"
          @click="navigateToNewStaff"
        />
      </div>
    </div>
    <remote-data-grid-columns-eight
      v-if="isCurrentStaff"
      :endpoint-url="staffEndpoint"
      :endpoint-params="currentStaffParams"
      :initial-sort="{dataId: 'first_name', direction: 'asc'}"
      search-placeholder="Search by First and/or Last Name or Email Address"
      search-by="name"
      search-name="staff-search"
      :is-search-input-only="true"
      :is-archived="false"
      :has-filters="true"
      filter-name="staff-table-options"
      :filter-options-url="filterOptionsEndpoint"
      :columns="currentColumns"
      :persisted-search-value="persistedSearchValue"
      data-testid="staff-table-current"
      @update-search-value="handleSearchValueUpdate"
    />
    <remote-data-grid-columns-eight
      v-else
      :endpoint-url="staffEndpoint"
      :endpoint-params="archivedStaffParams"
      :initial-sort="{dataId: 'first_name', direction: 'asc'}"
      search-placeholder="Search by First and/or Last Name or Email Address"
      search-by="name"
      search-name="staff-search"
      :is-search-input-only="true"
      :is-archived="true"
      :has-filters="true"
      filter-name="staff-table-options"
      :filter-options-url="filterOptionsEndpoint"
      :columns="archivedColumns"
      :persisted-search-value="persistedSearchValue"
      data-testid="staff-table-archived"
      @update-search-value="handleSearchValueUpdate"
    />
  </div>
</template>


<script setup>
import { ref, computed } from "vue";

const props = defineProps({
  canManageSystem: {
    type: Boolean,
    default: false
  }
});

const isCurrentStaff = ref(true);
const urlParams = new URLSearchParams(window.location.search);
const persistedSearchValue = ref(urlParams.get('name') || "");
const urlRoles = urlParams.getAll('roles[]');

const buildStaffParams = (status) => {
  const p = { status };
  if (urlRoles.length > 0) {
    p.filter = JSON.stringify({ roles: urlRoles });
  }
  return JSON.stringify(p);
};

const toggleCurrentStaff = () =>{
  isCurrentStaff.value = !isCurrentStaff.value;
};

const navigateToNewStaff = () => {
  window.location.href = '/staffsafe/admin/staff/new';
};

const handleSearchValueUpdate = (value) => {
  persistedSearchValue.value = value;
};

// Endpoint URL and params for current and archived staff
const staffEndpoint = "/staffsafe/staff/data_grid";
const currentStaffParams = buildStaffParams('current');
const archivedStaffParams = buildStaffParams('archived');
const filterOptionsEndpoint = computed(() => "/staffsafe/staff/filter_options");

const sharedColumns = [
  {
    dataId: "first_name_url",
    columnHeading: "First Name",
    sortable: true,
    sortBy: "first_name",
    columnType: "link",
    columnDataOptions:{
      columnButtonType: "inline-text",
      columnButtonSize: "small",
      columnButtonClass: "link-primary"
    }
  },
  {
    dataId: "last_name_url",
    columnHeading: "Last Name",
    sortable: true,
    sortBy: "last_name",
    columnType: "link",
    columnDataOptions:{
      columnButtonType: "inline-text",
      columnButtonSize: "small",
      columnButtonClass: "link-primary"
    }
  },
  { 
    dataId: "email", 
    columnHeading: "Email Address",
    sortable: true,
    columnType: "text",
    columnDataOptions:{}
  },
  { 
    dataId: "role", 
    columnHeading: "Job Role",
    sortable: true,
    columnType: "text",
    columnDataOptions:{}
  },
  { 
    dataId: "permission_group", 
    columnHeading: "Permission Group",
    sortable: true,
    columnType: "text",
    columnDataOptions:{}
  },
  {
    dataId: "login_access",
    columnHeading: "Login Access",
    sortable: true,
    columnType: "icon",
    columnDataOptions: {} // Icon colors handled by CSS
  }
];

const actionsColumn = {
  dataId: "actions",
  columnHeading: "Options",
  sortable: false,
  columnType: "actions",
  columnDataOptions: {}
};

const currentColumns = [
  ...sharedColumns,
  {
    dataId: "compliant",
    columnHeading: "Compliant",
    sortable: true,
    columnType: "icon",
    columnDataOptions: {} // Icon colors handled by CSS
  },
  actionsColumn
];

const archivedColumns = [
  ...sharedColumns,
  {
    dataId: "archived_at",
    columnHeading: "Archived at",
    sortable: true,
    columnType: "text",
    columnDataOptions:{}
  },
  actionsColumn
];
</script>


<style lang="scss" scoped>
  :deep(.f-filter-bar){
    @media (max-width: 840px){
      display:block;
    }
  }

  :deep(.filter-button),
  :deep(.more-filters .more-filters_toggle){
    @media (max-width: 840px){
      margin-left: 0;
      margin-right: 8px
    }
  }

  :deep(.data-grid_body td.data-grid-td::first-letter){
    text-transform:capitalize;
  }

  :deep(.data-grid_border){
    overflow: visible;
  }
</style>
